import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { Card, CardContent } from '@/components/ui/card';
import { Bell, ChevronRight, Globe, Palette } from 'lucide-react';
import { ProfileNotifications } from './ProfileNotifications';
import { cn } from '@/lib/utils';

type SettingsSection = 'main' | 'notifications';

export const ProfileSettings: React.FC = () => {
  const { t } = useTranslation('profile');
  const [currentSection, setCurrentSection] = useState<SettingsSection>('main');
  const isNative = Capacitor.getPlatform() !== 'web';

  if (currentSection === 'notifications') {
    return (
      <div className="space-y-4">
        <button
          onClick={() => setCurrentSection('main')}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronRight className="h-4 w-4 rotate-180" />
          {t('settings.backToSettings')}
        </button>
        <ProfileNotifications />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">{t('settings.title')}</h2>
      
      <Card>
        <CardContent className="p-0 divide-y divide-border">
          {/* Notifications - Only on native */}
          {isNative && (
            <button
              onClick={() => setCurrentSection('notifications')}
              className={cn(
                "w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors text-left"
              )}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-primary/10">
                  <Bell className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">{t('settings.notificationsSection')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.notificationsDescription')}
                  </p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </button>
          )}

          {/* Language - Coming soon */}
          <div className="w-full flex items-center justify-between p-4 opacity-50 cursor-not-allowed">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-secondary/50">
                <Globe className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="font-medium">{t('settings.languageSection')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('settings.comingSoon')}
                </p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </div>

          {/* Theme - Coming soon */}
          <div className="w-full flex items-center justify-between p-4 opacity-50 cursor-not-allowed">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-secondary/50">
                <Palette className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="font-medium">{t('settings.themeSection')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('settings.comingSoon')}
                </p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </div>
        </CardContent>
      </Card>

      {/* Empty state for web */}
      {!isNative && (
        <p className="text-center text-sm text-muted-foreground py-4">
          {t('settings.moreSettingsComingSoon')}
        </p>
      )}
    </div>
  );
};
